import { useState, useEffect, useCallback } from "react";
import apiService from "../../../service/Api";
import { IStreamDetails } from "../../../types/streams";
import {
  CrewEntry,
  CrewEntryResponse,
} from "../components/callsheet/types/callsheet";
import { INetworkInfo, IStream, IStreamsResponse } from "../types/view-stream";

export interface TimeAllocation {
  call_time?: string;
  departure_time?: string;
  arrival_at_venue_time?: string;
  setup_from_time?: string;
  setup_to_time?: string;
  shoot_start_time?: string;
  shoot_end_time?: string;
  estimated_wrap_time?: string;
  back_to_base_time?: string;
  unpacking_time?: string;
  depart_time?: string;
}

export const useStreamData = (id: string | undefined) => {
  const [stream, setStream] = useState<IStreamDetails | null>(null);
  const [crewMembersData, setCrewMembersData] = useState<CrewEntry[]>([]);
  const [streams, setStreams] = useState<IStream[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeData, setTimeData] = useState({
    call_time: "N/A",
    departure_time: "N/A",
    arrival_at_venue_time: "N/A",
    setup_from_time: "N/A",
    setup_to_time: "N/A",
    shoot_start_time: "N/A",
    shoot_end_time: "N/A",
    estimated_wrap_time: "N/A",
    back_to_base_time: "N/A",
    unpacking_time: "N/A",
    depart_time: "N/A",
  });
  const [networkData, setNetworkData] = useState<INetworkInfo[]>([]);

  const fetchData = useCallback(async () => {
    if (!id) {
      setError("Invalid stream ID");
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    let streamData: IStreamDetails | null = null;

    try {
      // Fetch stream details
      const streamResponse = await apiService.get<IStreamDetails>(
        `/api/streams/stream/${id}/`
      );
      streamData = streamResponse.data;
      setStream(streamData);
    } catch (err: any) {
      console.error("Stream details fetch failed", err);
      setError("Failed to load stream details.");
    }

    try {
      // Fetch networking data
      const response = await apiService.get<{ results: INetworkInfo[] }>(
        `/api/streams/networking/?stream=${id}`
      );
      setNetworkData(response.data.results);
    } catch (error) {
      console.error("Failed to fetch networking data", error);
    }

    try {
      // Fetch all streams and time allocation
      const streamsResponse = await apiService.get<IStreamsResponse>(
        "/api/streams/stream/"
      );
      const validStreams = streamsResponse.data.results.filter(
        (s) => s?.id && s?.title && s?.date
      );
      setStreams(validStreams);

      // Fetch time allocation
      const timeResponse = await apiService.get<TimeAllocation>(
        `/api/streams/stream/${id}/time-algorithim/`
      );
      const timeJson = timeResponse.data;
      setTimeData({
        call_time: timeJson.call_time || "N/A",
        departure_time: timeJson.departure_time || "N/A",
        arrival_at_venue_time: timeJson.arrival_at_venue_time || "N/A",
        setup_from_time: timeJson.setup_from_time || "N/A",
        setup_to_time: timeJson.setup_to_time || "N/A",
        shoot_start_time:
          timeJson.shoot_start_time || streamData?.start || "N/A",
        shoot_end_time: timeJson.shoot_end_time || streamData?.end || "N/A",
        estimated_wrap_time: timeJson.estimated_wrap_time || "N/A",
        back_to_base_time: timeJson.back_to_base_time || "N/A",
        unpacking_time: timeJson.unpacking_time || "N/A",
        depart_time: timeJson.depart_time || "N/A",
      });
    } catch (err: any) {
      console.error("Streams list or time allocation fetch failed", err);
      setError("Failed to load streams data or time allocation.");
    }

    try {
      // Fetch assigned crew members
      const res = await apiService.get<CrewEntryResponse>(
        `/api/call-sheets/callsheet/?stream=${id}`
      );
      const crewData = res.data.results as CrewEntry[];
      setCrewMembersData(crewData);
    } catch (error) {
      console.error("Error fetching assigned crew members:", error);
      setError("Failed to load assigned crew members.");
    }

    setIsLoading(false);
  }, [id]);

  const fetchNetworkData = async () => {
    if (!id) return;
    try {
      const response = await apiService.get<{ results: INetworkInfo[] }>(
        `/api/streams/networking/?stream=${id}`
      );
      setNetworkData(response.data.results);
    } catch (error) {
      console.error("Failed to fetch networking data", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    stream,
    crewMembersData,
    streams,
    isLoading,
    error,
    timeData,
    networkData,
    fetchData,
    fetchNetworkData,
  };
};
